import * as React from 'react';
import { createPortal } from 'react-dom';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import Avatar from 'antd/lib/avatar';
import Button from 'antd/lib/button';
import Checkbox from 'antd/lib/checkbox';
import Dropdown from 'antd/lib/dropdown';
import Input from 'antd/lib/input';
import Select from 'antd/lib/select';
import Text from 'antd/lib/typography/Text';
import Tooltip from 'antd/lib/tooltip';
import {
    AimOutlined,
    BugOutlined,
    BulbFilled,
    CaretDownOutlined,
    CheckCircleFilled,
    CheckCircleOutlined,
    ClockCircleFilled,
    ClockCircleOutlined,
    CloseCircleFilled,
    CloseCircleOutlined,
    EllipsisOutlined,
    ExperimentOutlined,
    FlagOutlined,
    InfoCircleOutlined,
    LineChartOutlined,
    LoadingOutlined,
    MonitorOutlined,
    PicRightOutlined,
    PlusCircleOutlined,
    ReconciliationOutlined,
    RobotOutlined,
    SearchOutlined,
    SendOutlined,
    StarOutlined,
    TagOutlined,
    TeamOutlined,
    ToolFilled,
    ToolOutlined,
    UndoOutlined,
    ZoomInOutlined,
    ZoomOutOutlined,
} from '@ant-design/icons';
import {
    antGold,
    antGrey,
    antMagenta,
    colorWithAlpha,
    getAvatarColorForName,
    runwayColors,
    rwColors,
} from '@/components/styles/globalStyles';
import IntegrationIcon from '@/components/icons/IntegrationIcon';
import runwayLogo from '../../../assets/integration-runway.svg';
import { IntegrationType, IssueEntity, IssueSourceType, IssueState, OrgUser, ReleaseStepType } from '@/types';
import { RootState } from '@/reducers/reducers';
import {
    selectAppStoreType,
    selectAppTimeFormatSetting,
    selectEffectiveTimezone,
    selectReleasesForAppId,
    selectRequiredFixRequestsApprovals,
    selectUserById,
    selectUserCanPerformAction,
} from '@/selectors/selectors';
import {
    GetFixRequestAuthorIds,
    GetFixRequestCurrentApprovals,
    getFixRequestFormFieldsSummary,
    getIntegrationTypeForAppStoreType,
} from '@/helpers';
import { iconAndAltForIntegrationType } from '@/helpers/integrationHelpers';
import { routeForReleaseStepOrNav } from '@/helpers/routeHelpers';
import { RunwayUserAction } from '@/helpers/aclHelpers';
import { DISALLOWED_ACTION_ERROR_MESSAGE } from '@/components/HOCs/PermissionedActionButton';
import { formatDateWithTimezone } from '@/timeHelpers';
import { selectAppOrgUsers, selectSortedGroups } from '@/selectors/memoizedSelectors';
import {
    TRY_REOPEN_ISSUE,
    TRY_TRANSITION_ISSUE,
    TRY_UPDATE_ISSUE,
    TRY_UPDATE_ISSUE_ASSIGNEES,
    TriageViewType,
    tryReopenIssue,
    tryTransitionIssue,
    tryUpdateIssue,
    tryUpdateIssueAssignees,
} from '@/actions/triage';
import { displayTextForUserChecklistGroup, displayUserNameLong } from '@/helpers/displayHelpers';
import { useIsLoading } from '@/utils/hooks/helpers';
import { RequestFixModal } from './RequestFixModal';
import { useHistory } from 'react-router-dom';
import { AddSourceModal } from './AddSourceModal';

const stopCardClick = (e: React.MouseEvent) => e.stopPropagation();

export const LABEL_STYLE: React.CSSProperties = {
    color: antGrey.grey8,
    fontSize: 10,
    fontWeight: 600,
    letterSpacing: 1,
    textTransform: 'uppercase',
};

const CARD_COL = { status: 200, reporter: 100, assignee: 100, version: 160, actions: 56 };
const cardGridColumns = (hasReporter: boolean, preview?: boolean) => {
    const cols = [`${CARD_COL.status}px`, 'minmax(0, 1fr)'];
    if (hasReporter && !preview) cols.push(`${CARD_COL.reporter}px`);
    cols.push(`${CARD_COL.assignee}px`);
    if (!preview) cols.push(`${CARD_COL.version}px`, `${CARD_COL.actions}px`);
    return cols.join(' ');
};

const UserAvatarWithTooltip: React.FC<{
    user?: OrgUser;
    fallbackName?: string;
    size?: number;
    extraTooltip?: string;
    bordered?: boolean;
}> = ({ user, fallbackName, size = 28, extraTooltip, bordered }) => {
    const name = user ? displayUserNameLong(user) : (fallbackName ?? '');
    const bgColor = getAvatarColorForName(name);
    const tooltip =
        extraTooltip ?
            <>
                {name ?
                    <>
                        {name}
                        <br />
                    </>
                :   null}
                {extraTooltip}
            </>
        :   name || undefined;
    const avatar = (
        <Avatar
            size={size}
            src={user?.commsAvatars?.image32}
            style={{ backgroundColor: bgColor, flexShrink: 0, fontSize: 12, border: 'none' }}
        >
            {name.charAt(0).toUpperCase()}
        </Avatar>
    );
    return (
        <Tooltip title={tooltip} placement="top">
            {bordered ?
                <span
                    style={{
                        display: 'inline-flex',
                        borderRadius: '50%',
                        padding: 2,
                        background: `repeating-conic-gradient(${bgColor} 0deg 6deg, transparent 6deg 15deg)`,
                    }}
                >
                    {avatar}
                </span>
            :   avatar}
        </Tooltip>
    );
};

// Placeholder source-type icons — to be swapped for Matt's per-source icons.
const SOURCE_TYPE_ICON: Record<IssueSourceType, React.ReactNode> = {
    [IssueSourceType.Runway]: <TeamOutlined />,
    [IssueSourceType.PMTicket]: <TagOutlined />,
    [IssueSourceType.UserReviews]: <StarOutlined />,
    [IssueSourceType.BetaFeedback]: <ExperimentOutlined />,
    [IssueSourceType.RegressionTesting]: <ReconciliationOutlined />,
    [IssueSourceType.StabMon]: <BugOutlined />,
    [IssueSourceType.OBAA]: <LineChartOutlined />,
};

const SOURCE_TYPE_LABEL: Record<IssueSourceType, string> = {
    [IssueSourceType.Runway]: 'Runway',
    [IssueSourceType.PMTicket]: 'Ticket',
    [IssueSourceType.UserReviews]: 'User reviews',
    [IssueSourceType.BetaFeedback]: 'Beta feedback',
    [IssueSourceType.RegressionTesting]: 'Regression testing',
    [IssueSourceType.StabMon]: 'Stability monitoring',
    [IssueSourceType.OBAA]: 'Observability & analytics',
};

interface StateMeta {
    label: string;
    icon: React.ReactNode;
}

export const stateMeta = (state: IssueState): StateMeta => {
    switch (state) {
        case IssueState.New:
            return { label: 'Needs triage', icon: <SendOutlined style={{ color: rwColors.primary, fontSize: 16 }} /> };
        case IssueState.Assigned:
            return {
                label: 'Assigned',
                icon: <MonitorOutlined style={{ color: rwColors.statusYellow, fontSize: 16 }} />,
            };
        case IssueState.FixRequested:
            return {
                label: 'Fix requested',
                icon: <ToolFilled style={{ color: rwColors.statusYellow, fontSize: 16 }} />,
            };
        case IssueState.FixApproved:
            return {
                label: 'Fix approved',
                icon: <CheckCircleFilled style={{ color: rwColors.statusGreen, fontSize: 16 }} />,
            };
        case IssueState.Reopened:
            return {
                label: 'Reopened',
                icon: <UndoOutlined style={{ color: rwColors.statusYellow, fontSize: 16 }} />,
            };
        case IssueState.FixMerged:
            return {
                label: 'Resolved',
                icon: <CheckCircleOutlined style={{ color: rwColors.statusGreen, fontSize: 16 }} />,
            };
        case IssueState.FixRejected:
            return {
                label: 'Fix rejected',
                icon: <CloseCircleFilled style={{ color: rwColors.statusRed, fontSize: 16 }} />,
            };
        case IssueState.NotAPriority:
        default:
            return {
                label: 'Not a priority',
                icon: <CloseCircleOutlined style={{ color: antGrey.grey6, fontSize: 16 }} />,
            };
    }
};

export const TriageIssueCard: React.FC<{
    issue: IssueEntity;
    orgId: string;
    preview?: boolean;
    disabled?: boolean;
    viewType: TriageViewType;
    basePath?: string;
    selectMode?: boolean;
    selected?: boolean;
    selectDisabled?: boolean;
    onToggleSelected?: () => void;
}> = ({
    issue,
    orgId,
    preview,
    disabled,
    viewType,
    basePath,
    selectMode,
    selected,
    selectDisabled,
    onToggleSelected,
}) => {
    const history = useHistory();
    const hasFixRequest = !!issue.fixRequest;
    // Dim the whole card for deprioritized / rejected issues to signal they won't be acted on.
    const isDimmed = issue.state === IssueState.FixRejected || issue.state === IssueState.NotAPriority;
    const openDrawer = () => {
        if (!preview && basePath) history.push(`${basePath}/${issue.id}`);
    };
    const card = (
        <div
            onClick={preview ? undefined : openDrawer}
            style={{
                border: `1px solid ${antGrey.grey5}`,
                borderRadius: 8,
                overflow: 'hidden',
                backgroundColor: antGrey.grey2,
                cursor: preview ? undefined : 'pointer',
                ...(selectMode ? { flex: 1, minWidth: 0 } : {}),
            }}
        >
            {/* Dim closed/deprioritized issues per-section so the reopen / overflow actions stay fully visible. */}
            {hasFixRequest && !preview && (
                <FixRequestModule issue={issue} orgId={orgId} dimmed={isDimmed} disabled={disabled} />
            )}
            <IssueBody
                issue={issue}
                orgId={orgId}
                preview={preview}
                disabled={disabled}
                viewType={viewType}
                dimmed={isDimmed}
            />
        </div>
    );
    if (!selectMode) {
        return card;
    }
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Checkbox checked={!!selected} disabled={selectDisabled} onChange={() => onToggleSelected?.()} />
            {card}
        </div>
    );
};

interface FixRequestStatusMeta {
    label: string;
    icon: React.ReactNode;
    pending: boolean;
}

export const fixRequestStatusMeta = (state: IssueState): FixRequestStatusMeta => {
    switch (state) {
        case IssueState.FixApproved:
            return {
                label: 'Fix approved',
                icon: <CheckCircleFilled style={{ color: rwColors.statusGreen, fontSize: 16 }} />,
                pending: false,
            };
        case IssueState.FixMerged:
            return {
                label: 'Fix merged',
                icon: <CheckCircleFilled style={{ color: rwColors.statusGreen, fontSize: 16 }} />,
                pending: false,
            };
        case IssueState.FixRejected:
            return {
                label: 'Fix rejected',
                icon: <CloseCircleFilled style={{ color: rwColors.statusRed, fontSize: 16 }} />,
                pending: false,
            };
        case IssueState.FixRequested:
        default:
            return {
                label: 'Pending approval',
                icon: <ClockCircleFilled style={{ color: rwColors.statusYellow, fontSize: 16 }} />,
                pending: state === IssueState.FixRequested,
            };
    }
};

const FixRequestModule: React.FC<{ issue: IssueEntity; orgId: string; dimmed?: boolean; disabled?: boolean }> = ({
    issue,
    orgId,
    dimmed,
    disabled,
}) => {
    const dispatch = useDispatch();
    const history = useHistory();
    const fixRequest = issue.fixRequest;
    const meta = fixRequestStatusMeta(issue.state);
    const requesterId = fixRequest?.createdBy;
    const requester = useSelector((state: RootState) =>
        requesterId ? selectUserById(state, orgId, issue.appId, requesterId) : undefined,
    );
    const status = fixRequest?.status;
    const approverIds = status ? GetFixRequestAuthorIds(status) : [];
    const deciderId = approverIds.length > 0 ? approverIds[approverIds.length - 1] : undefined;
    const decider = useSelector((state: RootState) =>
        deciderId ? selectUserById(state, orgId, issue.appId, deciderId) : undefined,
    );
    const isRejected = issue.state === IssueState.FixRejected;
    const showDecided =
        !!deciderId && (isRejected || issue.state === IssueState.FixApproved || issue.state === IssueState.FixMerged);
    const requiredApprovals = useSelector(selectRequiredFixRequestsApprovals(issue.appId)) || 1;
    const currentApprovals = status ? GetFixRequestCurrentApprovals(status) : 0;
    const approverUsers = useSelector(
        (state: RootState) => approverIds.map((id) => selectUserById(state, orgId, issue.appId, id)),
        shallowEqual,
    );
    const showApprovalCount = requiredApprovals > 1;
    const effectiveTimezone = useSelector((state: RootState) => selectEffectiveTimezone(state, issue.appId));
    const use24HourTimeFormat = useSelector((state: RootState) => selectAppTimeFormatSetting(state, issue.appId));
    const decidedAtLabel =
        showDecided && status?.updatedAt ?
            formatDateWithTimezone(status.updatedAt, 'longMonthDateYearTime', effectiveTimezone, use24HourTimeFormat)
        :   undefined;
    const isTransitioning = useIsLoading(TRY_TRANSITION_ISSUE, issue.id);
    const canApproveReject = useSelector((state: RootState) =>
        selectUserCanPerformAction(state, RunwayUserAction.UAUpdateFixRequestStatus),
    );
    const isApprovedLike = issue.state === IssueState.FixApproved || issue.state === IssueState.FixMerged;
    const showApproveReject = meta.pending || isRejected || isApprovedLike;
    const canTransition = meta.pending && canApproveReject && !disabled;
    const transition = (targetState: IssueState) =>
        dispatch(tryTransitionIssue({ orgId, appId: issue.appId, issueId: issue.id, targetState }));
    const stop = (e: React.MouseEvent) => e.stopPropagation();
    const hasReporter = !!issue.reporter;
    const fixRequestSummary = getFixRequestFormFieldsSummary(fixRequest?.formFieldsValues);
    const goToFeatureReadiness = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!fixRequest?.fixVersion) {
            return;
        }
        const base = routeForReleaseStepOrNav(
            orgId,
            issue.appId,
            fixRequest.fixVersion,
            ReleaseStepType.FeatureReadiness,
        );
        // Deep-link the fix's item so Feature Readiness opens it directly (workItems on the full issue, workItemIds
        // on the lighter list payload). Falls back to the step summary when neither is available.
        const itemId = fixRequest.workItems?.[0]?.id ?? fixRequest.workItemIds?.[0];
        history.push(itemId ? `${base}?${new URLSearchParams({ selectedItem: itemId }).toString()}` : base);
    };

    const approverAvatars = (
        <div style={{ display: 'flex' }}>
            {approverIds.map((id, idx) => (
                <div
                    key={id}
                    style={{
                        marginLeft: idx === 0 ? 0 : -8,
                        border: `2px solid ${antGrey.grey3}`,
                        borderRadius: '50%',
                        display: 'inline-flex',
                    }}
                >
                    <UserAvatarWithTooltip user={approverUsers[idx]} fallbackName={id} size={20} />
                </div>
            ))}
        </div>
    );

    // Same column tracks as the body (cardGridColumns) so status / content / version / actions line up by
    // construction — no pixel offsets. Each cell just fills its track.
    return (
        <div
            style={{
                display: 'grid',
                gridTemplateColumns: cardGridColumns(hasReporter, false),
                alignItems: 'center',
                backgroundColor: antGrey.grey3,
                borderBottom: `1px solid ${antGrey.grey5}`,
                cursor: 'pointer',
                opacity: dimmed ? 0.6 : undefined,
            }}
        >
            {/* status: fix-request status label */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, padding: '12px 8px 12px 16px' }}>
                {meta.icon}
                <Text style={LABEL_STYLE}>{meta.label}</Text>
            </div>

            {/* content: feature-readiness jump link + requester + summary. The link sits in a 24px slot (like the
                integration icon below) so it centers on that icon and the requester/summary start at the title. */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, minWidth: 0, padding: '12px 16px' }}>
                <div style={{ width: 16, flexShrink: 0, display: 'flex', justifyContent: 'center' }}>
                    {fixRequest?.fixVersion && (
                        <Tooltip title="View feature readiness" placement="top">
                            <PicRightOutlined
                                onClick={goToFeatureReadiness}
                                style={{ color: rwColors.primary, fontSize: 16, cursor: 'pointer' }}
                            />
                        </Tooltip>
                    )}
                </div>
                <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                    {requesterId && <UserAvatarWithTooltip user={requester} fallbackName={requesterId} size={20} />}
                    <div style={{ flex: 1, minWidth: 0 }}>
                        {fixRequestSummary && (
                            <Text ellipsis style={{ color: antGrey.grey8, fontSize: 13 }}>
                                {fixRequestSummary}
                            </Text>
                        )}
                    </div>
                </div>
            </div>

            {/* reporter column placeholder — keeps alignment when the body shows a reporter */}
            {hasReporter && <div />}

            {/* assignee: approval status + count + approvers */}
            <div
                onClick={stop}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    gap: 12,
                    minWidth: 0,
                    padding: '12px 8px',
                }}
            >
                {meta.pending && showApprovalCount && (
                    <>
                        <ClockCircleOutlined style={{ color: rwColors.statusYellow, fontSize: 13 }} />
                        <Text style={{ color: antGrey.grey8, fontSize: 14, whiteSpace: 'nowrap' }}>
                            {currentApprovals} of {requiredApprovals}
                        </Text>
                        {approverIds.length > 0 && approverAvatars}
                        <span
                            style={{
                                width: 1,
                                height: 20,
                                backgroundColor: antGrey.grey5,
                                flexShrink: 0,
                                // Even ~20px air on both sides: +8 completes the 12 gap to the count (=20), and -4
                                // pulls the bar near the cell edge so the gap to the aim is also ~20.
                                marginLeft: 8,
                                marginRight: -4,
                            }}
                        />
                    </>
                )}
                {showDecided && (
                    <>
                        {isRejected ?
                            <CloseCircleOutlined style={{ color: rwColors.statusRed, fontSize: 16 }} />
                        :   <CheckCircleOutlined style={{ color: rwColors.statusGreen, fontSize: 16 }} />}
                        {!isRejected && showApprovalCount && (
                            <Text style={{ color: antGrey.grey8, fontSize: 12, whiteSpace: 'nowrap' }}>
                                {currentApprovals} of {requiredApprovals}
                            </Text>
                        )}
                        {!isRejected && showApprovalCount && approverIds.length > 0 ?
                            approverAvatars
                        :   <UserAvatarWithTooltip
                                user={decider}
                                fallbackName={deciderId}
                                size={20}
                                extraTooltip={decidedAtLabel}
                            />
                        }
                    </>
                )}
            </div>

            {/* version: disabled target fix-version select (lines up with the version select below) */}
            <div
                onClick={stop}
                style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '12px 0 12px 16px' }}
            >
                {fixRequest?.fixVersion && (
                    <div style={{ position: 'relative', width: 104 }}>
                        <div
                            style={{
                                position: 'absolute',
                                left: -20,
                                top: '50%',
                                transform: 'translateY(-50%)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 7,
                            }}
                        >
                            <Tooltip title="Target fix version" placement="top">
                                <AimOutlined style={{ color: antGrey.grey8, fontSize: 13 }} />
                            </Tooltip>
                        </div>
                        <Select
                            size="small"
                            disabled
                            value={fixRequest?.fixVersion ?? undefined}
                            options={
                                fixRequest?.fixVersion ?
                                    [{ value: fixRequest.fixVersion, label: fixRequest.fixVersion }]
                                :   []
                            }
                            style={{ width: 106 }}
                            popupMatchSelectWidth={false}
                            labelRender={({ label }) => (
                                <span style={{ fontWeight: 600, fontSize: 12, color: antGrey.grey8 }}>{label}</span>
                            )}
                            suffixIcon={
                                <CaretDownOutlined
                                    style={{ color: antGrey.grey6, fontSize: 16, pointerEvents: 'none' }}
                                />
                            }
                        />
                    </div>
                )}
            </div>

            {/* actions: approve / reject */}
            <div
                onClick={stop}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    gap: 8,
                    padding: '12px 10px 12px 0',
                }}
            >
                {showApproveReject && (
                    <>
                        <Tooltip
                            title={
                                !meta.pending ? undefined
                                : canApproveReject ?
                                    'Approve fix'
                                :   DISALLOWED_ACTION_ERROR_MESSAGE
                            }
                            placement="top"
                        >
                            <Button
                                size="small"
                                shape="circle"
                                loading={isTransitioning}
                                disabled={!canTransition}
                                onClick={() => transition(IssueState.FixApproved)}
                                icon={
                                    isTransitioning ? undefined : (
                                        <CheckCircleOutlined
                                            style={{
                                                color: isApprovedLike ? rwColors.statusGreen : antGrey.grey6,
                                                fontSize: 20,
                                            }}
                                        />
                                    )
                                }
                                style={{
                                    width: 20,
                                    height: 20,
                                    minWidth: 20,
                                    backgroundColor: runwayColors.white,
                                    boxShadow: `0 0 0 2px ${runwayColors.white}`,
                                    opacity: canTransition ? undefined : 0.4,
                                }}
                            />
                        </Tooltip>
                        <Tooltip
                            title={
                                !meta.pending ? undefined
                                : canApproveReject ?
                                    'Reject fix'
                                :   DISALLOWED_ACTION_ERROR_MESSAGE
                            }
                            placement="top"
                        >
                            <Button
                                size="small"
                                type="text"
                                shape="circle"
                                disabled={!canTransition}
                                onClick={() => transition(IssueState.FixRejected)}
                                icon={
                                    <CloseCircleOutlined
                                        style={{ color: isRejected ? rwColors.statusRed : antGrey.grey6, fontSize: 20 }}
                                    />
                                }
                                style={{
                                    width: 20,
                                    height: 20,
                                    minWidth: 20,
                                    backgroundColor: runwayColors.white,
                                    boxShadow: `0 0 0 2px ${runwayColors.white}`,
                                    opacity: canTransition ? undefined : 0.4,
                                }}
                            />
                        </Tooltip>
                    </>
                )}
            </div>
        </div>
    );
};

const IssueBody: React.FC<{
    issue: IssueEntity;
    orgId: string;
    preview?: boolean;
    disabled?: boolean;
    viewType: TriageViewType;
    dimmed?: boolean;
}> = ({ issue, orgId, preview, disabled, viewType, dimmed }) => {
    const hasReporter = !!issue.reporter;
    // Cells share cardGridColumns so the fix-request bar aligns with these columns. Each cell owns its own
    // background + padding (grid stretches them to full row height — no negative-margin bleed needed).
    // Everything dims for closed issues except the actions column, which stays fully visible.
    return (
        <div
            style={{
                display: 'grid',
                gridTemplateColumns: cardGridColumns(hasReporter, preview),
                backgroundColor: antGrey.grey2,
            }}
        >
            <StatusColumn issue={issue} dimmed={dimmed} />
            <ContentColumn issue={issue} dimmed={dimmed} />
            {hasReporter && !preview && <ReporterSection issue={issue} orgId={orgId} dimmed={dimmed} />}
            <AssigneeSection
                issue={issue}
                orgId={orgId}
                preview={preview}
                disabled={disabled}
                viewType={viewType}
                dimmed={dimmed}
            />
            {!preview && <VersionColumn issue={issue} orgId={orgId} dimmed={dimmed} disabled={disabled} />}
            {!preview && <ActionsColumn issue={issue} orgId={orgId} viewType={viewType} disabled={disabled} />}
        </div>
    );
};

const StatusColumn: React.FC<{ issue: IssueEntity; dimmed?: boolean }> = ({ issue, dimmed }) => {
    const meta = stateMeta(issue.state);
    const sources = issue.sources ?? [];
    const distinctSourceTypes = Array.from(new Set(sources.map((s) => s.type)));
    const hasPotentialCause = !!issue.potentialCauses?.causes?.length;
    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 12,
                padding: '20px 16px 16px',
                backgroundColor: antGrey.grey2,
                opacity: dimmed ? 0.6 : undefined,
            }}
        >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {meta.icon}
                <Text style={LABEL_STYLE}>{meta.label}</Text>
                {hasPotentialCause && (
                    <span
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginLeft: 'auto',
                            padding: 3,
                            borderRadius: 2,
                            backgroundColor: antGold.gold5,
                        }}
                    >
                        <BulbFilled style={{ color: runwayColors.white, fontSize: 14 }} />
                    </span>
                )}
            </div>
            {sources.length > 0 && (
                <Tooltip
                    placement="top"
                    title={
                        sources.length === 1 ?
                            SOURCE_TYPE_LABEL[sources[0].type]
                        :   `${sources.length} associated issues`
                    }
                >
                    <div
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 6,
                            padding: '4px',
                            border: `1px solid ${antGrey.grey6}`,
                            borderRadius: 4,
                            backgroundColor: antGrey.grey1,
                            alignSelf: 'flex-start',
                            marginTop: 'auto',
                            color: antGrey.grey6,
                            fontSize: 14,
                        }}
                    >
                        {sources.length > 1 && (
                            <Text style={{ fontSize: 16, color: antGrey.grey6, fontWeight: 600 }}>
                                {sources.length}x
                            </Text>
                        )}
                        {distinctSourceTypes.map((type) => (
                            <span key={type} style={{ display: 'inline-flex' }}>
                                {SOURCE_TYPE_ICON[type]}
                            </span>
                        ))}
                    </div>
                </Tooltip>
            )}
        </div>
    );
};

const ContentColumn: React.FC<{ issue: IssueEntity; dimmed?: boolean }> = ({ issue, dimmed }) => {
    const source = issue.sources?.[0];
    const description = issue.description;
    const title = issue.title;
    const appStoreType = useSelector((state: RootState) => selectAppStoreType(state, issue.appId));
    // User reviews / beta feedback carry no integrationType; derive it (store for reviews, TestFlight for
    // beta feedback) so IntegrationIcon shows the right icon instead of the generic fallback.
    const derivedIntegrationType =
        source?.type === IssueSourceType.UserReviews && appStoreType ? getIntegrationTypeForAppStoreType(appStoreType)
        : source?.type === IssueSourceType.BetaFeedback ? IntegrationType.TestFlight
        : null;
    const sourceIcon =
        source?.type === IssueSourceType.Runway ?
            <img src={runwayLogo} alt="Runway" width={16} height={16} />
        :   <IntegrationIcon integrationId={derivedIntegrationType ?? source?.integrationType} size={16} />;
    const [descTruncated, setDescTruncated] = React.useState(false);
    // expandedRect = the content row (drives the integration-icon vertical pin); cellRect = the full white
    // content cell, which stretches to the module height (drives the overlay edges + the zoom icon in the corner).
    const [expandedRect, setExpandedRect] = React.useState<DOMRect | null>(null);
    const [cellRect, setCellRect] = React.useState<DOMRect | null>(null);
    const [overlayVisible, setOverlayVisible] = React.useState(false);
    const descRef = React.useRef<HTMLDivElement>(null);
    const cellRef = React.useRef<HTMLDivElement>(null);
    const descTextRef = React.useRef<HTMLDivElement>(null);
    const overlayRef = React.useRef<HTMLDivElement>(null);
    const openOverlay = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (descRef.current) setExpandedRect(descRef.current.getBoundingClientRect());
        if (cellRef.current) setCellRect(cellRef.current.getBoundingClientRect());
    };
    const closeOverlay = () => {
        // Fade out, then unmount once the transition finishes.
        setOverlayVisible(false);
        window.setTimeout(() => setExpandedRect(null), 150);
    };
    // Fade the overlay in on the frame after it mounts (opacity 0 → 1), mirroring the phased-release widget.
    React.useEffect(() => {
        if (!expandedRect) return undefined;
        const id = requestAnimationFrame(() => setOverlayVisible(true));
        return () => cancelAnimationFrame(id);
    }, [expandedRect]);
    // Detect truncation of the CSS-clamped description ourselves (antd's JS ellipsis measured unreliably).
    React.useLayoutEffect(() => {
        const el = descTextRef.current;
        if (!el) return undefined;
        const check = () => setDescTruncated(el.scrollHeight > el.clientHeight + 1);
        check();
        const ro = new ResizeObserver(check);
        ro.observe(el);
        return () => ro.disconnect();
    }, [description]);
    React.useEffect(() => {
        if (!expandedRect) return undefined;
        // Position is captured at open time, so page scroll/resize would desync it — hard-close immediately.
        const hardClose = () => {
            setOverlayVisible(false);
            setExpandedRect(null);
        };
        // But scrolling *inside* the overlay (long descriptions scroll internally) must not close it.
        const onScroll = (e: Event) => {
            if (overlayRef.current && overlayRef.current.contains(e.target as Node)) return;
            hardClose();
        };
        window.addEventListener('scroll', onScroll, true);
        window.addEventListener('resize', hardClose);
        return () => {
            window.removeEventListener('scroll', onScroll, true);
            window.removeEventListener('resize', hardClose);
        };
    }, [expandedRect]);
    return (
        <div
            ref={cellRef}
            style={{
                position: 'relative',
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                padding: 16,
                backgroundColor: runwayColors.white,
                opacity: dimmed ? 0.6 : undefined,
            }}
        >
            <div ref={descRef} style={{ display: 'flex', alignItems: 'center', gap: 16, minWidth: 0 }}>
                <div style={{ width: 16, flexShrink: 0, display: 'flex', justifyContent: 'center', lineHeight: 0 }}>
                    {sourceIcon}
                </div>
                <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {title && (
                        <Text style={{ fontSize: 14, fontWeight: 500, color: antGrey.grey10, wordBreak: 'break-word' }}>
                            {title}
                        </Text>
                    )}
                    {description && (
                        <div
                            ref={descTextRef}
                            style={{
                                color: antGrey.grey7,
                                fontSize: 12,
                                lineHeight: '18px',
                                wordBreak: 'break-word',
                                display: '-webkit-box',
                                WebkitLineClamp: 3,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                paddingRight: 22,
                            }}
                        >
                            {description}
                        </div>
                    )}
                </div>
            </div>
            {/* Expand affordance pinned to the module's bottom-right corner (the cell stretches full height). */}
            {descTruncated && (
                <ZoomInOutlined
                    onClick={openOverlay}
                    style={{
                        position: 'absolute',
                        bottom: 12,
                        right: 20,
                        color: antGrey.grey6,
                        fontSize: 14,
                        cursor: 'pointer',
                    }}
                />
            )}
            {expandedRect &&
                cellRect &&
                createPortal(
                    <>
                        {/* Click-away layer (stopPropagation: portal events bubble up the React tree to the card) */}
                        <div
                            onClick={(e) => {
                                e.stopPropagation();
                                closeOverlay();
                            }}
                            style={{ position: 'fixed', inset: 0, zIndex: 1000 }}
                        />
                        {/* Full description, positioned over the description so it reads as the box expanding in place. */}
                        <div
                            ref={overlayRef}
                            onClick={stopCardClick}
                            style={{
                                position: 'fixed',
                                boxSizing: 'border-box',
                                top: cellRect.top - 1,
                                left: cellRect.left - 1,
                                width: cellRect.width + 2,
                                zIndex: 1001,
                                backgroundColor: runwayColors.white,
                                border: `1px solid ${antGrey.grey5}`,
                                borderRadius: '0 0 6px 6px',
                                boxShadow: 'var(--rw-shadow-md)',
                                opacity: overlayVisible ? 1 : 0,
                                transition: 'opacity 0.15s ease-in-out',
                            }}
                        >
                            <div
                                style={{
                                    maxHeight: 320,
                                    overflowY: 'auto',
                                    padding: 16,
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: 16,
                                }}
                            >
                                <div
                                    style={{
                                        width: 16,
                                        flexShrink: 0,
                                        display: 'flex',
                                        justifyContent: 'center',
                                        lineHeight: 0,
                                        // Pin the icon at the collapsed row's centered position so it doesn't shift
                                        // down to the middle of the (taller) expanded text.
                                        marginTop: Math.max(0, expandedRect.height / 2 - 8),
                                    }}
                                >
                                    {sourceIcon}
                                </div>
                                <div
                                    style={{
                                        flex: 1,
                                        minWidth: 0,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 8,
                                        paddingRight: 20,
                                    }}
                                >
                                    {title && (
                                        <Text
                                            style={{
                                                fontSize: 14,
                                                fontWeight: 500,
                                                color: antGrey.grey10,
                                                wordBreak: 'break-word',
                                            }}
                                        >
                                            {title}
                                        </Text>
                                    )}
                                    <Text style={{ color: antGrey.grey7, fontSize: 12, whiteSpace: 'pre-wrap' }}>
                                        {description}
                                    </Text>
                                </div>
                            </div>
                        </div>
                        <ZoomOutOutlined
                            onClick={(e) => {
                                e.stopPropagation();
                                closeOverlay();
                            }}
                            style={{
                                position: 'fixed',
                                top: cellRect.bottom - 26,
                                left: cellRect.right - 34,
                                zIndex: 1002,
                                padding: 2,
                                backgroundColor: runwayColors.white,
                                color: antGrey.grey6,
                                fontSize: 14,
                                cursor: 'pointer',
                                opacity: overlayVisible ? 1 : 0,
                                transition: 'opacity 0.15s ease-in-out',
                            }}
                        />
                    </>,
                    document.body,
                )}
        </div>
    );
};

const ColumnHeader: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <Text style={{ ...LABEL_STYLE, fontSize: 11 }}>{children}</Text>
);

const ReporterSection: React.FC<{ issue: IssueEntity; orgId: string; dimmed?: boolean }> = ({
    issue,
    orgId,
    dimmed,
}) => {
    // issue.reporter is a userId; fall back to the source's free-form reporter string when unresolved.
    const reporter = useSelector((state: RootState) =>
        issue.reporter ? selectUserById(state, orgId, issue.appId, issue.reporter) : undefined,
    );
    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: 16,
                padding: '16px 8px 16px 16px',
                backgroundColor: colorWithAlpha(antGrey.grey2, 0.5),
                opacity: dimmed ? 0.6 : undefined,
            }}
        >
            <ColumnHeader>Reporter</ColumnHeader>
            <UserAvatarWithTooltip user={reporter} fallbackName={issue.reporter} />
        </div>
    );
};

const ASSIGNEE_USER_PREFIX = 'user:';
const ASSIGNEE_GROUP_PREFIX = 'group:';

const AssigneeSection: React.FC<{
    issue: IssueEntity;
    orgId: string;
    preview?: boolean;
    disabled?: boolean;
    viewType: TriageViewType;
    dimmed?: boolean;
}> = ({ issue, orgId, preview, disabled, viewType, dimmed }) => {
    const dispatch = useDispatch();
    const assigneeId = issue.assigneeUserIds?.[0];
    const assigneeGroupId = issue.assigneeGroupIds?.[0];
    const suggestedId = issue.suggestedAssigneeUserIds?.[0];
    const assignee = useSelector((state: RootState) =>
        assigneeId ? selectUserById(state, orgId, issue.appId, assigneeId) : undefined,
    );
    const appUsers = useSelector((state: RootState) => selectAppOrgUsers(state, issue.appId));
    const groups = useSelector((state: RootState) => selectSortedGroups(state, orgId));
    const assignedGroup = assigneeGroupId ? groups.find((g) => g.id === assigneeGroupId) : undefined;
    const isAssigning = useIsLoading(TRY_UPDATE_ISSUE_ASSIGNEES, issue.id);
    const canManage = useSelector((state: RootState) =>
        selectUserCanPerformAction(state, RunwayUserAction.UAManageTriageIssues),
    );

    const assign = (assigneeUserIds: string[], assigneeGroupIds: string[]) =>
        dispatch(
            tryUpdateIssueAssignees({
                orgId,
                appId: issue.appId,
                issueId: issue.id,
                assigneeUserIds,
                assigneeGroupIds,
                viewType,
            }),
        );

    const onAssigneeSelected = (key: string) => {
        if (key.startsWith(ASSIGNEE_GROUP_PREFIX)) {
            assign([], [key.slice(ASSIGNEE_GROUP_PREFIX.length)]);
        } else {
            assign([key.slice(ASSIGNEE_USER_PREFIX.length)], []);
        }
    };

    const [assigneeSearch, setAssigneeSearch] = React.useState('');

    const menuLabel = (text: string) => (
        <span
            style={{
                display: 'block',
                maxWidth: 150,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                fontSize: 15,
                color: antGrey.grey8,
            }}
        >
            {text}
        </span>
    );

    const assigneeEntries = [
        ...appUsers.map((user) => {
            const name = displayUserNameLong(user);
            return {
                key: `${ASSIGNEE_USER_PREFIX}${user.id}`,
                name,
                label: menuLabel(name),
                icon: (
                    <Avatar
                        size={28}
                        src={user.commsAvatars?.image32}
                        style={{ backgroundColor: getAvatarColorForName(name), fontSize: 12, border: 'none' }}
                    >
                        {name.charAt(0).toUpperCase()}
                    </Avatar>
                ),
            };
        }),
        ...groups
            .filter((group) => !group.disabled)
            .map((group) => ({
                key: `${ASSIGNEE_GROUP_PREFIX}${group.id}`,
                name: displayTextForUserChecklistGroup(group),
                label: menuLabel(displayTextForUserChecklistGroup(group)),
                icon: (
                    <Avatar
                        size={28}
                        icon={<TeamOutlined />}
                        style={{ backgroundColor: antMagenta.magenta5, fontSize: 12, border: 'none' }}
                    />
                ),
            })),
    ];

    const assigneeMenuItems = assigneeEntries
        .filter(({ name }) => name.toLowerCase().includes(assigneeSearch.trim().toLowerCase()))
        .map(({ key, label, icon }) => ({ key, label, icon }));

    const selectedAssigneeKey =
        assigneeId ? `${ASSIGNEE_USER_PREFIX}${assigneeId}`
        : assigneeGroupId ? `${ASSIGNEE_GROUP_PREFIX}${assigneeGroupId}`
        : undefined;

    let avatar: React.ReactNode;
    if (assigneeId) {
        avatar = <UserAvatarWithTooltip user={assignee} fallbackName={assigneeId} bordered={issue.isAutoAssigned} />;
    } else if (assigneeGroupId) {
        avatar = (
            <Tooltip
                title={assignedGroup ? displayTextForUserChecklistGroup(assignedGroup) : assigneeGroupId}
                placement="top"
            >
                <Avatar
                    size={28}
                    style={{ backgroundColor: antMagenta.magenta5, fontSize: 12 }}
                    icon={<TeamOutlined />}
                />
            </Tooltip>
        );
    } else if (suggestedId) {
        avatar = (
            <Tooltip title="Suggested assignee" placement="top">
                <Avatar size={28} style={{ backgroundColor: rwColors.statusRed }} icon={<RobotOutlined />} />
            </Tooltip>
        );
    } else {
        avatar = (
            <span
                style={{
                    width: 28,
                    height: 28,
                    boxSizing: 'border-box',
                    borderRadius: '50%',
                    border: `1.5px dashed ${antGrey.grey6}`,
                    backgroundColor: runwayColors.white,
                    display: 'inline-block',
                }}
            />
        );
    }

    return (
        <div
            onClick={stopCardClick}
            style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: 16,
                padding: '16px 8px',
                backgroundColor: antGrey.grey2,
                opacity: dimmed ? 0.6 : undefined,
            }}
        >
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <ColumnHeader>Assignee</ColumnHeader>
                {issue.isAutoAssigned && (
                    <Tooltip title="Auto-assigned by Runway" placement="top">
                        <InfoCircleOutlined style={{ color: antGrey.grey6, fontSize: 12 }} />
                    </Tooltip>
                )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
                {avatar}
                {preview ?
                    null
                : isAssigning ?
                    <LoadingOutlined spin style={{ color: antGrey.grey6, fontSize: 18 }} />
                :   <Dropdown
                        trigger={['click']}
                        placement="bottomRight"
                        disabled={!canManage || disabled}
                        onOpenChange={(open) => {
                            if (!open) setAssigneeSearch('');
                        }}
                        menu={{
                            items: assigneeMenuItems,
                            selectedKeys: selectedAssigneeKey ? [selectedAssigneeKey] : [],
                            onClick: ({ key }) => onAssigneeSelected(key),
                            style: { maxHeight: 320, overflowY: 'auto' },
                        }}
                        dropdownRender={(menu) => (
                            <div
                                style={{
                                    backgroundColor: antGrey.grey1,
                                    borderRadius: 8,
                                    border: `1px solid ${antGrey.grey5}`,
                                    boxShadow: `0 6px 16px 0 ${colorWithAlpha(antGrey.grey8, 0.12)}`,
                                    overflow: 'hidden',
                                }}
                            >
                                <div style={{ padding: 8 }}>
                                    <Input
                                        autoFocus
                                        allowClear
                                        size="small"
                                        placeholder="Search..."
                                        prefix={<SearchOutlined style={{ color: antGrey.grey6 }} />}
                                        value={assigneeSearch}
                                        onChange={(e) => setAssigneeSearch(e.target.value)}
                                        onClick={(e) => e.stopPropagation()}
                                        onKeyDown={(e) => e.stopPropagation()}
                                    />
                                </div>
                                {assigneeMenuItems.length > 0 ?
                                    menu
                                :   <div
                                        style={{
                                            padding: '8px 12px 12px',
                                            color: antGrey.grey6,
                                            fontSize: 13,
                                        }}
                                    >
                                        No matches
                                    </div>
                                }
                            </div>
                        )}
                    >
                        <Tooltip title={canManage ? undefined : DISALLOWED_ACTION_ERROR_MESSAGE}>
                            <CaretDownOutlined
                                style={{
                                    color: antGrey.grey5,
                                    fontSize: 22,
                                    cursor: canManage && !disabled ? 'pointer' : 'not-allowed',
                                }}
                            />
                        </Tooltip>
                    </Dropdown>
                }
            </div>
        </div>
    );
};

const BuildConnector: React.FC = () => {
    const dot = <span style={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: antGrey.grey5 }} />;
    return (
        <div
            style={{
                position: 'absolute',
                left: -20,
                top: '50%',
                transform: 'translateY(-50%)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
            }}
        >
            {dot}
            <span style={{ height: 20, width: 1, backgroundColor: antGrey.grey5 }} />
            {dot}
        </div>
    );
};

const BuildRow: React.FC<{ value?: string; url?: string; icon?: React.ReactNode; dashedBottom?: boolean }> = ({
    value,
    url,
    icon,
    dashedBottom,
}) => {
    const label = value ? `#${value}` : '--';
    return (
        <div
            style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '4px 10px',
                borderBottom: dashedBottom ? `1px dashed ${antGrey.grey5}` : undefined,
                cursor: 'default',
            }}
        >
            {value && icon}
            {value && url ?
                <a
                    href={url}
                    target="_blank"
                    rel="noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    style={{ color: rwColors.primary, fontSize: 13, fontWeight: 500 }}
                >
                    {label}
                </a>
            :   <Text style={{ color: value ? antGrey.grey9 : antGrey.grey6, fontSize: 12 }}>{label}</Text>}
        </div>
    );
};

const VersionColumn: React.FC<{ issue: IssueEntity; orgId: string; dimmed?: boolean; disabled?: boolean }> = ({
    issue,
    orgId,
    dimmed,
    disabled,
}) => {
    const dispatch = useDispatch();
    const version = issue.versions?.[0];
    const build = issue.build;
    const ciIdentifier = build?.identifier ?? issue.builds?.[0];
    const [ciLogo] =
        build?.integrationId ? iconAndAltForIntegrationType(build.integrationId as IntegrationType) : [undefined];
    const [storeLogo] =
        build?.appStoreIntegrationId ?
            iconAndAltForIntegrationType(build.appStoreIntegrationId as IntegrationType)
        :   [undefined];
    const releases = useSelector((state: RootState) => selectReleasesForAppId(state, issue.appId));
    const isUpdating = useIsLoading(TRY_UPDATE_ISSUE, issue.id);
    const canManage = useSelector((state: RootState) =>
        selectUserCanPerformAction(state, RunwayUserAction.UAManageTriageIssues),
    );

    const versionOptions = releases.map((release) => ({
        value: release.version,
        label: release.displayableVersion || release.version,
    }));

    const selectVersion = (newVersion: string) =>
        dispatch(tryUpdateIssue({ orgId, appId: issue.appId, issueId: issue.id, version: newVersion }));

    return (
        <div
            onClick={stopCardClick}
            style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 6,
                padding: '12px 0 16px 16px',
                backgroundColor: antGrey.grey3,
                opacity: dimmed ? 0.6 : undefined,
            }}
        >
            <div style={{ position: 'relative', width: 106 }}>
                {/* Flag aligned to the build connector's left offset for a consistent left rail. */}
                <div
                    style={{
                        position: 'absolute',
                        left: -20,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 7,
                    }}
                >
                    <Tooltip title="First detected version" placement="top">
                        <FlagOutlined style={{ color: antGrey.grey7, fontSize: 13 }} />
                    </Tooltip>
                </div>
                <Tooltip title={canManage ? undefined : DISALLOWED_ACTION_ERROR_MESSAGE}>
                    <Select
                        size="small"
                        showSearch
                        optionFilterProp="label"
                        value={version}
                        options={versionOptions}
                        disabled={!canManage || disabled}
                        placeholder="--"
                        style={{ width: 106 }}
                        popupMatchSelectWidth={false}
                        labelRender={({ label, value }) => (
                            <span style={{ fontWeight: 600, fontSize: 12, color: antGrey.grey8 }}>
                                {label ?? value}
                            </span>
                        )}
                        suffixIcon={
                            isUpdating ?
                                <LoadingOutlined
                                    spin
                                    style={{ color: antGrey.grey6, fontSize: 16, pointerEvents: 'none' }}
                                />
                            :   <CaretDownOutlined
                                    style={{ color: antGrey.grey6, fontSize: 16, pointerEvents: 'none' }}
                                />
                        }
                        onChange={(value) => selectVersion(value)}
                    />
                </Tooltip>
            </div>
            {/* Build box — top: CI build (link + integration); bottom: matching App Store build. Dashed when empty. */}
            <div style={{ position: 'relative', width: 106 }}>
                <BuildConnector />
                <div
                    style={{
                        backgroundColor: runwayColors.white,
                        border: `1px ${ciIdentifier ? 'solid' : 'dashed'} ${antGrey.grey5}`,
                        borderRadius: 6,
                        overflow: 'hidden',
                    }}
                >
                    <BuildRow
                        value={ciIdentifier}
                        url={build?.url}
                        icon={ciLogo ? <img src={ciLogo} width={18} height={18} alt="" /> : undefined}
                        dashedBottom
                    />
                    <BuildRow
                        value={build?.appStoreBuild}
                        icon={storeLogo ? <img src={storeLogo} width={18} height={18} alt="" /> : undefined}
                    />
                </div>
            </div>
        </div>
    );
};

const CLOSED_STATES = [IssueState.FixMerged, IssueState.FixRejected, IssueState.NotAPriority];

// Menu key for "Request approval for a fix" — not a transition (opens a modal, wired in a later PR).
const REQUEST_FIX_KEY = 'requestFix';
const ADD_SOURCE_KEY = 'addSource';

const ActionsColumn: React.FC<{
    issue: IssueEntity;
    orgId: string;
    viewType: TriageViewType;
    disabled?: boolean;
}> = ({ issue, orgId, viewType, disabled }) => {
    const dispatch = useDispatch();
    const isAssigned = (issue.assigneeUserIds?.length ?? 0) > 0;
    const isClosed = CLOSED_STATES.includes(issue.state);
    // Whether a button (reopen / not-a-priority / request-fix) sits above the overflow "..." menu.
    const hasTopAction = isClosed || !isAssigned || !issue.fixRequest;
    const isTransitioning = useIsLoading(TRY_TRANSITION_ISSUE, issue.id);
    const isReopening = useIsLoading(TRY_REOPEN_ISSUE, issue.id);
    const canApproveReject = useSelector((state: RootState) =>
        selectUserCanPerformAction(state, RunwayUserAction.UAUpdateFixRequestStatus),
    );
    const canManage = useSelector((state: RootState) =>
        selectUserCanPerformAction(state, RunwayUserAction.UAManageTriageIssues),
    );
    const [requestFixOpen, setRequestFixOpen] = React.useState(false);
    const [addSourceOpen, setAddSourceOpen] = React.useState(false);

    const transition = (targetState: IssueState) =>
        dispatch(tryTransitionIssue({ orgId, appId: issue.appId, issueId: issue.id, targetState, viewType }));
    const reopen = () => dispatch(tryReopenIssue({ orgId, appId: issue.appId, issueId: issue.id, viewType }));

    const menuLabel = (text: string) => <span style={{ color: antGrey.grey8 }}>{text}</span>;
    // Wraps a menu label in the no-permission tooltip when the gating permission is missing.
    const gatedLabel = (text: string, allowed: boolean) =>
        allowed ?
            menuLabel(text)
        :   <Tooltip title={DISALLOWED_ACTION_ERROR_MESSAGE} placement="left">
                {menuLabel(text)}
            </Tooltip>;
    let fixMenuItems;
    if (!issue.fixRequest) {
        fixMenuItems = [
            {
                key: REQUEST_FIX_KEY,
                disabled: !canManage,
                icon: <ToolOutlined style={{ color: antGrey.grey7 }} />,
                label: gatedLabel('Request approval for a fix', canManage),
            },
        ];
    } else if (issue.state === IssueState.FixRequested) {
        fixMenuItems = [
            {
                key: IssueState.FixApproved,
                disabled: !canApproveReject,
                icon: <CheckCircleOutlined style={{ color: antGrey.grey7 }} />,
                label: gatedLabel('Approve fix', canApproveReject),
            },
            {
                key: IssueState.FixRejected,
                disabled: !canApproveReject,
                icon: <CloseCircleOutlined style={{ color: antGrey.grey7 }} />,
                label: gatedLabel('Reject fix', canApproveReject),
            },
        ];
    } else {
        fixMenuItems = [];
    }
    const menuItems =
        isClosed ?
            []
        :   [
                ...fixMenuItems,
                {
                    key: ADD_SOURCE_KEY,
                    disabled: !canManage,
                    icon: <PlusCircleOutlined style={{ color: antGrey.grey7 }} />,
                    label: gatedLabel('Add source(s)...', canManage),
                },
                {
                    key: IssueState.NotAPriority,
                    disabled: !canManage,
                    icon: <CloseCircleOutlined style={{ color: antGrey.grey7 }} />,
                    label: gatedLabel("Mark as 'Not a priority'", canManage),
                },
                ...(issue.state === IssueState.FixApproved ?
                    [
                        {
                            key: IssueState.FixMerged,
                            disabled: !canManage,
                            icon: <CheckCircleOutlined style={{ color: antGrey.grey7 }} />,
                            label: gatedLabel("Mark as 'Resolved'", canManage),
                        },
                    ]
                :   []),
            ];
    const onMenuClick = ({ key }: { key: string }) => {
        if (key === REQUEST_FIX_KEY) {
            setRequestFixOpen(true);
            return;
        }
        if (key === ADD_SOURCE_KEY) {
            setAddSourceOpen(true);
            return;
        }
        // Every other key is the target IssueState.
        transition(key as IssueState);
    };

    return (
        <>
            <div
                onClick={stopCardClick}
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-end',
                    // Center the lone "..." vertically when there's no button above it.
                    justifyContent: hasTopAction ? 'flex-start' : 'center',
                    gap: 20,
                    padding: hasTopAction ? '30px 10px 16px 0' : '16px 10px 16px 0',
                    backgroundColor: antGrey.grey3,
                }}
            >
                {isClosed ?
                    <Tooltip title={canManage ? 'Reopen' : DISALLOWED_ACTION_ERROR_MESSAGE} placement="top">
                        <Button
                            size="small"
                            shape="circle"
                            loading={isReopening}
                            disabled={!canManage || disabled}
                            onClick={reopen}
                            icon={
                                isReopening ? undefined : (
                                    <UndoOutlined style={{ color: antGrey.grey6, fontSize: 11 }} />
                                )
                            }
                            style={{
                                width: 20,
                                height: 20,
                                minWidth: 20,
                                borderRadius: 16,
                                borderWidth: 1.5,
                                backgroundColor: runwayColors.white,
                                borderColor: antGrey.grey6,
                                boxShadow: `0 0 0 2px ${runwayColors.white}`,
                            }}
                        />
                    </Tooltip>
                : !isAssigned ?
                    <Tooltip
                        title={canManage ? "Mark as 'Not a priority'" : DISALLOWED_ACTION_ERROR_MESSAGE}
                        placement="top"
                    >
                        <Button
                            size="small"
                            type="text"
                            shape="circle"
                            loading={isTransitioning}
                            disabled={!canManage || disabled}
                            onClick={() => transition(IssueState.NotAPriority)}
                            icon={
                                isTransitioning ? undefined : (
                                    <CloseCircleOutlined style={{ color: antGrey.grey6, fontSize: 20 }} />
                                )
                            }
                            style={{
                                width: 20,
                                height: 20,
                                minWidth: 20,
                                backgroundColor: runwayColors.white,
                                boxShadow: `0 0 0 2px ${runwayColors.white}`,
                            }}
                        />
                    </Tooltip>
                : !issue.fixRequest ?
                    <Tooltip
                        title={canManage ? 'Request approval for a fix' : DISALLOWED_ACTION_ERROR_MESSAGE}
                        placement="top"
                    >
                        <Button
                            size="small"
                            shape="circle"
                            disabled={!canManage || disabled}
                            onClick={() => setRequestFixOpen(true)}
                            icon={<ToolFilled style={{ color: antGrey.grey6, fontSize: 11 }} />}
                            style={{
                                width: 20,
                                height: 20,
                                minWidth: 20,
                                borderRadius: 16,
                                backgroundColor: runwayColors.white,
                                borderColor: antGrey.grey6,
                                borderWidth: 1.5,
                                boxShadow: `0 0 0 2px ${runwayColors.white}`,
                            }}
                        />
                    </Tooltip>
                :   null}
                <Dropdown
                    trigger={['click']}
                    placement="bottomRight"
                    disabled={disabled}
                    menu={{ items: menuItems, onClick: onMenuClick }}
                >
                    <Button
                        size="small"
                        type="text"
                        loading={isTransitioning}
                        disabled={disabled}
                        icon={isTransitioning ? undefined : <EllipsisOutlined style={{ color: antGrey.grey7 }} />}
                        style={{ width: 20, height: 20, minWidth: 20, padding: 0 }}
                    />
                </Dropdown>
            </div>
            <div onClick={stopCardClick}>
                <RequestFixModal
                    open={requestFixOpen}
                    onClose={() => setRequestFixOpen(false)}
                    orgId={orgId}
                    appId={issue.appId}
                    issue={issue}
                    viewType={viewType}
                />
                <AddSourceModal
                    open={addSourceOpen}
                    onClose={() => setAddSourceOpen(false)}
                    orgId={orgId}
                    appId={issue.appId}
                    issueId={issue.id}
                />
            </div>
        </>
    );
};
